import { isAxiosError } from "axios";
import { Button, Modal } from "flowbite-react";
import { useState } from "react";
import { Control, Controller } from "react-hook-form";
import { EmailPreview } from "../../components/EmailPreview";
import { TextInput } from "../../components/form";
import { EmailTemplate } from "../../models/notification";
import notificationService from "../../services/notification-service";
import { Toast } from "../../services/toast-service";
import {
  AcademicEmailComposerFormValues,
  UnitOfStudy,
} from "./AcademicEmailComposerForm.Types";

interface AcademicEmailComposerFormStep3Props {
  control: Control<AcademicEmailComposerFormValues>;
  formValues: AcademicEmailComposerFormValues;
  emailTemplate: EmailTemplate;
  getEmailTemplateVariables: (values: any) => any;
  goBack: () => void;
  setLoading: (loading: boolean) => void;
  selectedFaculty: string;
  unitsOfStudy: UnitOfStudy[];
}

const AcademicEmailComposerFormStep3 = ({
  control,
  formValues,
  emailTemplate,
  getEmailTemplateVariables,
  goBack,
  setLoading,
  selectedFaculty,
  unitsOfStudy,
}: AcademicEmailComposerFormStep3Props) => {
  const [testEmail, setTestEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const cleanHTML = (html: string) => {
    // remove color and background-color styles
    // ref - https://docs.aws.amazon.com/waf/latest/developerguide/aws-managed-rule-groups-baseline.html
    return html.replace(/style="[^"]*"/g, "");
  };

  const handleSendTestEmail = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      setEmailError("Invalid email address");
      return;
    }

    setLoading(true);

    try {
      await notificationService.sendTestEmail({
        emailTo: testEmail,
        emailSubject: formValues.subject,
        emailBody: cleanHTML(formValues.personalMessage),
        unitOfStudy: formValues.unitOfStudy,
        emailTemplate: emailTemplate!.name,
        emailSignature: formValues.signature,
        emailTemplateVariables: {
          Faculty: selectedFaculty,
          Semester: formValues.semester,
          UnitOfStudyCode: formValues.unitOfStudy,
          UnitOfStudyName: unitsOfStudy?.find(
            (unit) => unit.value === formValues.unitOfStudy,
          )?.label,
          EmailSignature: cleanHTML(formValues.signature),
          Salutation: cleanHTML(formValues.salutation),
          PersonalMessage: cleanHTML(formValues.personalMessage),
          EmailSubject: cleanHTML(formValues.subject),
        },
        fromEmail: formValues.senderEmail,
        replyToEmail: formValues.replyTo,
        preferredName: "Test Name",
      });

      Toast("Success!!", "Your test email was sent.", "success");
    } catch (err) {
      console.error("sendTestEmail error:", err);

      let message = `Something went wrong. Please try again. ${err}`;
      if (isAxiosError(err)) {
        if (err.response?.data?.message) {
          message = err.response.data.message;
        } else if (err.response?.status === 400) {
          message = "Bad request. Please check the input fields.";
        } else if (err.response?.status === 401) {
          message = "Unauthorized. Please log in again.";
        } else if (err.response?.status === 403) {
          message =
            "The content provided was invalid. Please review and try again.";
        } else if (err.response?.status === 500) {
          message = "Server error. Please try again later.";
        }
      }

      Toast("Error!", message, "danger");
    }

    setLoading(false);

    setShowModal(false);
    setTestEmail("");
  };

  return (
    <div id="step-3" className="space-y-6">
      <div className="grid grid-cols-1">
        <div className="flex flex-row flex-1 gap-4">
          <div className="flex-1">
            <Controller
              name="subject"
              control={control}
              rules={{
                required: "Subject is required",
              }}
              render={({ field, fieldState: { error } }) => {
                const { name, value, onBlur, disabled, onChange } = field;
                return (
                  <TextInput
                    name={name}
                    value={value}
                    onBlur={onBlur}
                    onChange={onChange}
                    disabled={disabled}
                    required
                    label="Subject"
                    placeholder="Subject"
                    errorText={error?.message}
                  />
                );
              }}
            />
          </div>
          <div className="flex items-end mb-[14.5px]">
            <button
              type="button"
              className="flex flex-row gap-2 items-center p-2 px-4 bg-white border border-gray-300 rounded-lg h-10 "
              onClick={() => setShowModal(true)}
            >
              <img src="/images/mail.png" alt="mail" className="h-5" />
              Send a Test Email
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="senderEmail"
          control={control}
          rules={{
            required: "Sender email is required",
          }}
          render={({ field, fieldState: { error } }) => {
            const { name, value, onBlur, disabled, onChange } = field;
            return (
              <TextInput
                name={name}
                value={value}
                onBlur={onBlur}
                disabled={disabled}
                onChange={onChange}
                required
                label="Sender Email"
                placeholder="Sender Email"
                errorText={error?.message}
              />
            );
          }}
        />
        <Controller
          name="replyTo"
          control={control}
          rules={{
            required: "Reply to is required",
          }}
          render={({ field, fieldState: { error } }) => {
            const { name, value, onBlur, disabled, onChange } = field;
            return (
              <TextInput
                name={name}
                value={value}
                onBlur={onBlur}
                disabled={disabled}
                onChange={onChange}
                required
                label="Reply To"
                placeholder="Reply To"
                errorText={error?.message}
              />
            );
          }}
        />
        <Controller
          name="ccReplyTo"
          control={control}
          rules={{
            required: "CC reply to is required",
          }}
          render={({ field, fieldState: { error } }) => {
            const { name, value, onBlur, onChange } = field;
            return (
              <TextInput
                name={name}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                disabled
                required
                label="CC Reply To"
                placeholder="CC Reply To"
                errorText={error?.message}
              />
            );
          }}
        />
      </div>

      {emailTemplate && (
        <div className="flex rounded-md border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-800 flex-col">
          <EmailPreview
            emailTemplate={emailTemplate}
            emailTemplateVariables={getEmailTemplateVariables(formValues)}
            useEditorStyle={false}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <button
          type="button"
          onClick={goBack}
          className="text-brand bg-white border-2 border-brand focus:outline-none font-medium rounded-md text-md px-5 py-2.5 me-2 mb-2 w-full"
        >
          Back
        </button>

        <button
          name="next"
          type="submit"
          className="focus:outline-none text-white bg-brand font-medium rounded-md text-md px-5 py-2.5 me-2 mb-2 w-full"
        >
          Submit
        </button>
      </div>
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <div className="flex items-center w-full">
            <span className="text-lg font-medium">Send Test Email</span>
            <div className="px-5 ml-auto">
              <Button
                size="xs"
                className="focus:ring-0 focus:outline-none border-none shadow-none"
                onClick={() => setTestEmail(formValues.senderEmail)}
              >
                Send Me
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <TextInput
            type="email"
            label="Enter Test Email"
            placeholder="Enter email address"
            value={testEmail}
            onChange={(e) => {
              setTestEmail(e.target.value);
              setEmailError("");
            }}
            errorText={emailError}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSendTestEmail}>Send Test Email</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showConfirmation} onClose={() => setShowConfirmation(false)}>
        <Modal.Body>
          <div className="text-center text-lg font-semibold">
            Test email sent!
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowConfirmation(false)}>OK</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AcademicEmailComposerFormStep3;
